"use client";

import { Link } from "@/src/lib/next-compat";
import {
  ArrowRightIcon,
  ChevronDown,
  ChevronRight,
  Loader2,
  SparklesIcon,
} from "lucide-react";
import { getModuleStatusBadge } from "@/lib/admin/course-details";
import type {
  CanonicalGenerationStatus,
  GeneratedLesson,
  Lesson,
  Module,
  PlacementQuiz,
  Level,
} from "@/lib/admin/course-details";

export default function LessonsTab({
  canonicalStatus,
  canonicalProgressTotal,
  canonicalProgressGenerated,
  canonicalProgressPercent,
  isCanonicalFanoutSubmitting,
  handleGenerateCanonicalForCourse,
  hasActiveLessonFilters,
  filteredLessonsCount,
  totalLessons,
  lessonApprovalFilter,
  setLessonApprovalFilter,
  lessonQuizFilter,
  setLessonQuizFilter,
  lessonCanonicalFilter,
  setLessonCanonicalFilter,
  lessonTypeFilter,
  setLessonTypeFilter,
  lessonTypeOptions,
  placementQuizzes,
  courseId,
  onOpenPlacementTab,
  sortedLevelIds,
  filteredModulesByLevel,
  expandedModules,
  toggleModule,
  getStatusParams,
  getLessonStatus,
  getLessonQuizBadge,
  canonicalGeneratingByLessonId,
  generatingCanonicalLessonId,
  handleGenerateCanonical,
  handleViewLesson,
  resetLessonFilters,
}: {
  canonicalStatus: CanonicalGenerationStatus | null;
  canonicalProgressTotal: number;
  canonicalProgressGenerated: number;
  canonicalProgressPercent: number;
  isCanonicalFanoutSubmitting: boolean;
  handleGenerateCanonicalForCourse: (force: boolean) => void;
  hasActiveLessonFilters: boolean;
  filteredLessonsCount: number;
  totalLessons: number;
  lessonApprovalFilter: "all" | "approved" | "unapproved";
  setLessonApprovalFilter: (value: "all" | "approved" | "unapproved") => void;
  lessonQuizFilter: "all" | "with_quiz" | "no_quiz";
  setLessonQuizFilter: (value: "all" | "with_quiz" | "no_quiz") => void;
  lessonCanonicalFilter: "all" | "with_canonical" | "no_canonical";
  setLessonCanonicalFilter: (value: "all" | "with_canonical" | "no_canonical") => void;
  lessonTypeFilter: string;
  setLessonTypeFilter: (value: string) => void;
  lessonTypeOptions: string[];
  placementQuizzes: PlacementQuiz[];
  courseId: number;
  onOpenPlacementTab: () => void;
  sortedLevelIds: number[];
  filteredModulesByLevel: Record<number, { level: Level | null; modules: Module[] }>;
  expandedModules: Record<number, boolean>;
  toggleModule: (moduleId: number) => void;
  getStatusParams: (statusInput: string | undefined) => { color: string; label: string };
  getLessonStatus: (lesson: Lesson) => string;
  getLessonQuizBadge: (genLesson: GeneratedLesson | undefined) => { label: string; color: string; title?: string };
  canonicalGeneratingByLessonId: Record<number, boolean>;
  generatingCanonicalLessonId: number | null;
  handleGenerateCanonical: (lessonId: number, lessonTitle: string) => void;
  handleViewLesson: (lessonId: number, genLessonUuid: string | undefined, status: string) => void;
  resetLessonFilters: () => void;
}) {
  return (
    <>
      {canonicalStatus && canonicalProgressTotal > 0 && (
        <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-bold text-slate-900">Canonical lesson generation</h2>
              <p className="text-xs text-slate-500">
                {canonicalProgressGenerated} / {canonicalProgressTotal} generated
                {canonicalStatus.pending > 0 ? ` • ${canonicalStatus.pending} pending` : ""}
                {canonicalStatus.failed > 0 ? ` • ${canonicalStatus.failed} failed` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => handleGenerateCanonicalForCourse(false)}
                disabled={isCanonicalFanoutSubmitting || canonicalStatus.is_complete}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {canonicalStatus.is_complete ? "Completed" : "Generate"}
              </button>
              {canonicalStatus.failed > 0 && (
                <button
                  type="button"
                  onClick={() => handleGenerateCanonicalForCourse(true)}
                  disabled={isCanonicalFanoutSubmitting}
                  className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Retry failed
                </button>
              )}
            </div>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${canonicalProgressPercent}%` }} />
          </div>
        </div>
      )}

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-wide text-slate-600">Lesson filters</span>
          {hasActiveLessonFilters && (
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
              Showing {filteredLessonsCount} of {totalLessons} lessons
            </span>
          )}
          <select value={lessonApprovalFilter} onChange={(e) => setLessonApprovalFilter(e.target.value as "all" | "approved" | "unapproved")} className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="all">All approval states</option>
            <option value="approved">Approved only</option>
            <option value="unapproved">Unapproved only</option>
          </select>
          <select value={lessonQuizFilter} onChange={(e) => setLessonQuizFilter(e.target.value as "all" | "with_quiz" | "no_quiz")} className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="all">All quiz states</option>
            <option value="with_quiz">With quiz</option>
            <option value="no_quiz">No quiz</option>
          </select>
          <select value={lessonCanonicalFilter} onChange={(e) => setLessonCanonicalFilter(e.target.value as "all" | "with_canonical" | "no_canonical")} className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="all">All canonical states</option>
            <option value="with_canonical">Canonical generated</option>
            <option value="no_canonical">No canonical generated</option>
          </select>
          <select value={lessonTypeFilter} onChange={(e) => setLessonTypeFilter(e.target.value)} className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500">
            <option value="all">All lesson types</option>
            {lessonTypeOptions.map((lessonType) => (
              <option key={lessonType} value={lessonType}>{lessonType}</option>
            ))}
          </select>
          <button type="button" onClick={resetLessonFilters} className={`rounded-md px-2.5 py-1.5 text-sm font-medium transition-colors ${hasActiveLessonFilters ? "border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>Reset filters</button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-100 rounded-lg"><SparklesIcon className="h-5 w-5 text-slate-700" /></div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Placement Quiz</h2>
              <p className="text-sm text-slate-500">
                {placementQuizzes.length > 0
                  ? `${placementQuizzes.length} difficulty levels • ${placementQuizzes.reduce((acc, q) => acc + q._count.placement_questions, 0)} total questions`
                  : "Author static placement questions per level"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {placementQuizzes.length > 0 && (() => {
              const statuses = placementQuizzes.map((q) => q.status);
              let displayStatus = "APPROVED";
              if (statuses.includes("PENDING_REVIEW")) displayStatus = "PENDING_REVIEW";
              else if (statuses.includes("UNDER_REVIEW")) displayStatus = "UNDER_REVIEW";
              else if (statuses.includes("REJECTED")) displayStatus = "REJECTED";
              const { color, label } = getStatusParams(displayStatus);
              return <div className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border border-current bg-transparent ${color}`}>{label}</div>;
            })()}
            <button
              type="button"
              onClick={onOpenPlacementTab}
              className="text-sm font-semibold text-slate-800 hover:text-slate-950 flex items-center gap-1 transition-colors"
            >
              Manage placement quiz
              <ArrowRightIcon className="h-4 w-4" />
            </button>
            {placementQuizzes.length > 0 && (
              <Link to={`/admin/reviews/course_placement/${courseId}`} className="text-sm font-medium text-slate-500 hover:text-slate-800">
                Review
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-12">
        {sortedLevelIds.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">No lessons match the selected filters.</div>
        )}
        {sortedLevelIds.map((levelId) => {
          const group = filteredModulesByLevel[levelId];
          const levelName = group.level ? `${group.level.name}` : "General Modules";
          return (
            <div key={levelId} className="space-y-4">
              <div className="flex items-center gap-4 pb-2 border-b border-slate-200">
                <h2 className="text-xl font-bold text-slate-800">{levelName}</h2>
                {group.level && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">Level {group.level.level_number}</span>
                )}
              </div>

              <div className="space-y-4">
                {group.modules.map((module) => {
                  const moduleBadge = getModuleStatusBadge(module.lessons);
                  return (
                  <div key={module.id} className="bg-white rounded-xl overflow-hidden border border-slate-200 shadow-sm transition-shadow hover:shadow-md">
                    <button onClick={() => toggleModule(module.id)} className="w-full text-left px-5 py-4 bg-slate-50/90 border-b border-slate-200 hover:bg-slate-50 transition-colors flex items-center justify-between group">
                      <div className="flex-1 min-w-0 mr-4">
                        <h3 className="text-lg font-bold text-slate-900 truncate">{module.title}</h3>
                        <p className="text-xs text-slate-500 font-medium mt-1">{module.lessons.length} lessons</p>
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0">
                        <div className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border ${moduleBadge.color} ${moduleBadge.borderClass}`}>{moduleBadge.label}</div>
                        <div className="text-slate-400 group-hover:text-slate-600 transition-colors">
                          {expandedModules[module.id] === false ? <ChevronRight className="h-6 w-6" /> : <ChevronDown className="h-6 w-6" />}
                        </div>
                      </div>
                    </button>

                    {expandedModules[module.id] !== false && (
                      <div className="divide-y divide-slate-100 bg-white animate-in fade-in slide-in-from-top-1 duration-200">
                        {module.lessons.map((lesson) => {
                          const glRaw = lesson.generated_lessons;
                          const genLesson = Array.isArray(glRaw) ? glRaw[0] : glRaw ?? undefined;
                          const hasCanonical = Boolean(genLesson?.structured_content);
                          const status = getLessonStatus(lesson);
                          const isGeneratingCanonical = Boolean(canonicalGeneratingByLessonId[lesson.id]) && !hasCanonical;
                          return (
                            <div key={lesson.id} className="px-5 py-3.5 flex items-center justify-between gap-3 hover:bg-slate-50/90 transition-colors group">
                              <div className="flex-1 pr-4 min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                  <p className="text-sm font-medium text-slate-800 group-hover:text-slate-900 transition-colors truncate">{lesson.title}</p>
                                  {lesson.lesson_type && (
                                    <span className="shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-600">{lesson.lesson_type}</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 mt-1">
                                  {status === "APPROVED" && genLesson?.updated_at && (
                                    <p className="text-xs text-slate-500">Approved on {new Date(genLesson.updated_at).toLocaleDateString()}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 sm:gap-5 flex-wrap justify-end">
                                <div className="flex items-center gap-2 flex-wrap justify-end">
                                  {(() => {
                                    const { color, label } = getStatusParams(status);
                                    const borderClass = color.replace("text-", "border-");
                                    return <div className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border bg-transparent ${color} ${borderClass}`}>{label}</div>;
                                  })()}
                                  {isGeneratingCanonical && <div className="px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border bg-transparent text-blue-600 border-blue-200">Generating canonical…</div>}
                                  {(() => {
                                    const q = getLessonQuizBadge(genLesson);
                                    const borderClass = q.color.replace("text-", "border-");
                                    return <div className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold border bg-transparent ${q.color} ${borderClass}`} title={q.title ?? undefined}>{q.label}</div>;
                                  })()}
                                </div>
                                {!hasCanonical && (
                                  <button
                                    type="button"
                                    onClick={() => handleGenerateCanonical(lesson.id, lesson.title)}
                                    disabled={lesson._count.content_chunks < 1 || generatingCanonicalLessonId === lesson.id || isGeneratingCanonical}
                                    title={lesson._count.content_chunks < 1 ? "Upload lesson content first" : "Queue generate_canonical_lesson for this lesson"}
                                    className="text-xs font-semibold flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0 shadow-sm"
                                  >
                                    {generatingCanonicalLessonId === lesson.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <SparklesIcon className="h-3.5 w-3.5" />}
                                    Generate canonical
                                  </button>
                                )}
                                <button
                                  onClick={() => handleViewLesson(lesson.id, genLesson?.uuid, status)}
                                  className={`text-sm font-semibold flex items-center gap-1 transition-colors shrink-0 ${status !== "not_started" ? "text-slate-800 hover:text-slate-950" : "text-slate-300 cursor-not-allowed"}`}
                                  disabled={status === "not_started"}
                                >
                                  View
                                  <ArrowRightIcon className="h-3 w-3" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )})}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
